'use client';

import { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import Link from 'next/link';
import {
  MessageCircle,
  ChevronDown,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  getProductQA,
  createQuestion,
  answerQuestion,
  type QaItemResponse,
} from '@/lib/services/productService';
import { useAuth } from '@/lib/auth/auth-context';
import { AskQuestionModal } from './ask-question-modal';
import { QAAnswerForm } from './qa-answer-form';

// ============================================================================
// TYPES
// ============================================================================

interface ProductQASectionProps {
  productSlug: string;
  ownerId: number;
  founderName: string;
  founderAvatar?: string | null;
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function QAEmptyState({ onAskQuestion, isOwner }: { onAskQuestion: () => void; isOwner: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
        <MessageCircle className="w-6 h-6 text-foreground-secondary" />
      </div>
      <h3 className="text-base font-medium text-foreground mb-1">
        Вопросов пока нет
      </h3>
      <p className="text-sm text-foreground-tertiary mb-5 max-w-xs">
        {isOwner
          ? 'Здесь появятся вопросы от пользователей'
          : 'Станьте первым, кто задаст вопрос основателю'}
      </p>
      {!isOwner && (
        <Button onClick={onAskQuestion} variant="outline" size="sm" className="gap-2">
          <MessageCircle className="h-4 w-4" />
          Задать вопрос
        </Button>
      )}
    </div>
  );
}

function QALoadingSkeleton() {
  return (
    <div className="divide-y divide-border">
      {[1, 2, 3].map((i) => (
        <div key={i} className="px-6 py-4">
          <div className="flex items-start gap-3">
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-3 w-32" />
            </div>
            <Skeleton className="h-5 w-5" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// QA ITEM - Single Q&A accordion item
// ============================================================================

interface QAItemProps {
  qa: QaItemResponse;
  founderName: string;
  founderAvatar?: string | null;
  isOwner: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onAnswer: (answer: string) => Promise<void>;
  isAnswering: boolean;
}

function QAItem({
  qa,
  founderName,
  founderAvatar,
  isOwner,
  isExpanded,
  onToggle,
  onAnswer,
  isAnswering,
}: QAItemProps) {
  const isAnswered = !!qa.answer;
  const needsAnswer = !isAnswered && isOwner;
  const askerName = qa.askedBy?.name || 'Аноним';
  const askerId = qa.askedBy?.id;

  return (
    <div className={cn(needsAnswer && 'bg-amber-50/30 dark:bg-amber-950/10')}>
      {/* Question Row - Clickable */}
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-start gap-3 text-left hover:bg-muted/30 transition-colors"
      >
        {/* Question Content */}
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-foreground">{qa.question}</h3>
          <p className="text-xs text-foreground-tertiary mt-1">
            Спросил{' '}
            {askerId ? (
              <Link
                href={`/profile/${askerId}`}
                onClick={(e) => e.stopPropagation()}
                className="text-foreground-secondary hover:text-primary transition-colors"
              >
                {askerName}
              </Link>
            ) : (
              <span>{askerName}</span>
            )}
            {' · '}
            {formatDistanceToNow(new Date(qa.createdAt), { addSuffix: true, locale: ru })}
          </p>
        </div>

        {/* Status Indicator + Chevron */}
        <div className="flex items-center gap-2">
          {!isAnswered && (
            <span className="text-xs text-amber-600 dark:text-amber-400">
              Ожидает ответа
            </span>
          )}
          <ChevronDown
            className={cn(
              "h-5 w-5 text-foreground-tertiary transition-transform",
              isExpanded && "rotate-180"
            )}
          />
        </div>
      </button>

      {/* Answer Section - Expandable */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-4">
              {isAnswered ? (
                // Answered - Show founder's response
                <div className="flex items-start gap-3 ml-4 p-4 bg-muted/50 rounded-xl">
                  <Avatar className="h-8 w-8 flex-shrink-0">
                    {founderAvatar && (
                      <AvatarImage src={founderAvatar} alt={founderName} />
                    )}
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
                      {founderName.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{founderName}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded-md bg-emerald-600/90 text-white font-medium">
                        Основатель
                      </span>
                      {qa.answeredAt && (
                        <span className="text-xs text-foreground-tertiary">
                          {formatDistanceToNow(new Date(qa.answeredAt), { addSuffix: true, locale: ru })}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-foreground-secondary">{qa.answer}</p>
                  </div>
                </div>
              ) : isOwner ? (
                // Not answered + Owner - Show answer form
                <div className="ml-4 p-4 rounded-xl bg-muted/30 border-2 border-dashed border-amber-300/50 dark:border-amber-700/30">
                  <QAAnswerForm
                    onSubmit={onAnswer}
                    isSubmitting={isAnswering}
                  />
                </div>
              ) : (
                // Not answered + Visitor - Show waiting message
                <div className="ml-4 p-3 rounded-xl bg-muted/50 text-sm text-foreground-tertiary">
                  Ожидает ответа основателя
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ProductQASection({
  productSlug,
  ownerId,
  founderName,
  founderAvatar,
}: ProductQASectionProps) {
  const { isAuthenticated, user } = useAuth();
  const [qaItems, setQaItems] = useState<QaItemResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [answeringId, setAnsweringId] = useState<number | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  // Check if current user is the product owner
  const isOwner = isAuthenticated && user?.id === ownerId;

  // Toggle expansion of a Q&A item
  const toggleExpanded = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Fetch Q&A items on mount
  useEffect(() => {
    let isMounted = true;

    async function loadQA() {
      const result = await getProductQA(productSlug);
      if (isMounted && result.success && result.data) {
        const sorted = [...result.data.items].sort((a, b) => b.upvotes - a.upvotes);
        setQaItems(sorted);
        // Auto-expand first answered item if exists
        const firstAnswered = sorted.find(q => q.answer);
        if (firstAnswered) {
          setExpandedIds(new Set([firstAnswered.id]));
        }
      }
      if (isMounted) {
        setIsLoading(false);
      }
    }

    loadQA();
    return () => { isMounted = false; };
  }, [productSlug]);

  // Handle asking a new question
  const handleAskQuestion = () => {
    if (!isAuthenticated) {
      toast.error('Войдите, чтобы задать вопрос');
      return;
    }
    if (isOwner) {
      toast.error('Вы не можете задавать вопросы своему продукту');
      return;
    }
    setIsModalOpen(true);
  };

  // Submit new question
  const handleSubmitQuestion = async (question: string) => {
    setIsSubmitting(true);

    const result = await createQuestion(productSlug, question);
    setIsSubmitting(false);

    if (result.success && result.data) {
      toast.success('Вопрос отправлен!');
      setIsModalOpen(false);
      setQaItems((prev) => [result.data!, ...prev]);
      // Auto-expand the new question
      setExpandedIds((prev) => new Set([...prev, result.data!.id]));
    } else {
      toast.error(result.error?.message || 'Не удалось отправить вопрос');
    }
  };

  // Handle answering a question (owner only)
  const handleAnswerSubmit = async (qaId: number, answer: string) => {
    setAnsweringId(qaId);

    const result = await answerQuestion(qaId, answer);
    setAnsweringId(null);

    if (result.success && result.data) {
      toast.success('Ответ опубликован!');
      setQaItems((prev) =>
        prev.map((q) => (q.id === qaId ? result.data! : q))
      );
    } else {
      toast.error(result.error?.message || 'Не удалось отправить ответ');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <Skeleton className="h-6 w-48" />
        </div>
        <QALoadingSkeleton />
      </div>
    );
  }

  // Empty state
  if (qaItems.length === 0) {
    return (
      <>
        <div className="bg-card border border-border rounded-2xl">
          <QAEmptyState onAskQuestion={handleAskQuestion} isOwner={isOwner} />
        </div>
        <AskQuestionModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          onSubmit={handleSubmitQuestion}
          isSubmitting={isSubmitting}
        />
      </>
    );
  }

  return (
    <>
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            Вопросы основателю
          </h2>
          {!isOwner && (
            <Button onClick={handleAskQuestion} variant="outline" size="sm">
              <MessageCircle className="h-4 w-4 mr-2" />
              Задать вопрос
            </Button>
          )}
        </div>

        {/* Q&A List - Accordion Style */}
        <div className="divide-y divide-border">
          {qaItems.map((qa) => (
            <QAItem
              key={qa.id}
              qa={qa}
              founderName={founderName}
              founderAvatar={founderAvatar}
              isOwner={isOwner}
              isExpanded={expandedIds.has(qa.id)}
              onToggle={() => toggleExpanded(qa.id)}
              onAnswer={(answer) => handleAnswerSubmit(qa.id, answer)}
              isAnswering={answeringId === qa.id}
            />
          ))}
        </div>
      </div>

      <AskQuestionModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onSubmit={handleSubmitQuestion}
        isSubmitting={isSubmitting}
      />
    </>
  );
}
